import { NavLink, Link } from 'react-router-dom';
import { UserPlus, User } from 'lucide-react';
import SidebarMobileOverlay from './SidebarMobileOverlay';
import SidebarTopControls from './SidebarTopControls';
import SidebarAccountMenu from './SidebarAccountMenu';
import { useAuthStore } from '@/stores/auth-store';
import { useAdminPendingCount } from '@/features/joinRequest/api/joinRequestHooks';
import { useEnterprisePlan } from '@/hooks/useEnterprisePlan';
import { isAdminRole } from '@/utils/roles';
import { learnerUrl } from '@/config/portals';
import {
  adminTopLinks,
  courseStudioLink,
  pendingMembersLink,
  enterpriseNavGroups,
  type AdminNavLink,
} from '@/data/adminNavigation';

interface AdminSidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose: () => void;
  onToggleCollapse: () => void;
  user: {
    name: string;
    email: string;
    avatar?: string | null;
  };
  onLogout: () => void;
}

/**
 * Dedicated admin-console sidebar. Rendered only inside AdminLayout (which is
 * ADMIN-gated), so it carries zero learner chrome — no streak card, no main
 * /dashboard item, no "Upgrade to Pro" card, no learner Profile link.
 *
 * Layout: the enterprise console groups (Overview / People / Learning /
 * Insights) plus the admin-only surfaces (admin dashboard, course studio,
 * pending members). A "Switch to Personal" link at the bottom drops back into
 * the learner shell (/dashboard) — the URL, not a stored flag, decides which
 * shell you see (Phase 2 retired `sidebarMode`).
 */
export default function AdminSidebar({ isOpen, isCollapsed, onClose, onToggleCollapse, user, onLogout }: AdminSidebarProps) {
  const { user: authUser, currentOrganization } = useAuthStore();
  const isAdmin = isAdminRole(authUser?.roles);
  const { isEnterprise } = useEnterprisePlan();
  const hasEnterpriseAdminAccess = isAdmin && isEnterprise;
  // Admins in an org see the org's identity; the account menu here is
  // admin-appropriate (org name + logout) with no learner-specific items.
  const accountLabel = currentOrganization?.name ?? 'Admin Console';
  const { data: pendingCount = 0 } = useAdminPendingCount(authUser?.tenantId);

  // Enterprise console groups + top-level admin links come from the typed nav
  // data (src/data/adminNavigation.ts); the sidebar owns only presentation and
  // the two conditional cases (pending-members badge, standalone Course Studio).

  // Shared active/inactive styling for a nav link (icon + label, collapse-aware).
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-colors ${
      isActive
        ? 'bg-tutara-primary text-white'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg'
    }`;

  const AdminNavLinkItem = ({ link }: { link: AdminNavLink }) => {
    const Icon = link.icon;
    return (
      <NavLink
        to={link.to}
        title={isCollapsed ? link.label : undefined}
        data-testid={link.testid}
        className={linkClass}
        onClick={() => onClose()}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!isCollapsed && <span className="font-medium">{link.label}</span>}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      <SidebarMobileOverlay isOpen={isOpen} onClose={onClose} />

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-30 h-screen bg-white dark:bg-tutara-dark-mode-surface border-r border-gray-200 dark:border-tutara-dark-mode-border transform transition-all duration-300 lg:translate-x-0 lg:top-16 lg:h-[calc(100vh-4rem)] flex flex-col overflow-visible ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isCollapsed ? 'w-16' : 'w-64'}`}
      >
        <SidebarTopControls onClose={onClose} isCollapsed={isCollapsed} onToggleCollapse={onToggleCollapse} />

        {/* Navigation */}
        <nav className={`p-4 space-y-2 overflow-y-auto flex-1 ${isCollapsed ? 'px-2' : ''}`}>
          {/* Top-level admin links (admin dashboard, …) */}
          {adminTopLinks.map((link) => (
            <AdminNavLinkItem key={link.testid} link={link} />
          ))}

          {/* Course Studio — standalone for non-enterprise admins; enterprise admins
              get it inside the Learning group below (same testid, mutually exclusive). */}
          {!hasEnterpriseAdminAccess && <AdminNavLinkItem link={courseStudioLink} />}

          {/* Pending join-requests with live count badge */}
          <NavLink
            to={pendingMembersLink.to}
            title={isCollapsed ? pendingMembersLink.label : undefined}
            data-testid={pendingMembersLink.testid}
            className={linkClass}
            onClick={() => onClose()}
          >
            <div className="relative flex-shrink-0">
              <UserPlus className="h-5 w-5" />
              {isCollapsed && pendingCount > 0 && (
                <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-red-100 text-red-700">
                  {pendingCount}
                </span>
              )}
            </div>
            {!isCollapsed && (
              <>
                <span className="font-medium">{pendingMembersLink.label}</span>
                {pendingCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                    {pendingCount}
                  </span>
                )}
              </>
            )}
          </NavLink>

          {/* Enterprise console — Overview / People / Learning / Insights (enterprise plan only) */}
          {hasEnterpriseAdminAccess && (
            <>
              {enterpriseNavGroups.map((group) => (
                <div key={group.key}>
                  {!isCollapsed && (
                    <div className="mt-4 mb-2 px-4">
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {group.label}
                      </span>
                    </div>
                  )}
                  {isCollapsed && <div className="my-3 border-t border-gray-200 dark:border-tutara-dark-mode-border" />}
                  <div className="space-y-1">
                    {group.links.map((link) => (
                      <AdminNavLinkItem key={link.testid} link={link} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Switch to Personal — cross-shell link into the learner app. In
              production the learner app lives on the bare host and the admin
              console on the `admin.` subdomain, so this crosses origins (an
              <a>, not a <Link>); on localhost it stays a same-origin path. */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-tutara-dark-mode-border">
            {(() => {
              const target = learnerUrl('/dashboard');
              const className = `flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-4 py-3 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg`;
              return target.external ? (
                <a
                  href={target.href}
                  title={isCollapsed ? 'Switch to Personal' : undefined}
                  data-testid="sidebar-switch-personal-link"
                  className={className}
                  onClick={() => onClose()}
                >
                  <User className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span className="font-medium">Switch to Personal</span>}
                </a>
              ) : (
                <Link
                  to={target.href}
                  title={isCollapsed ? 'Switch to Personal' : undefined}
                  data-testid="sidebar-switch-personal-link"
                  className={className}
                  onClick={() => onClose()}
                >
                  <User className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span className="font-medium">Switch to Personal</span>}
                </Link>
              );
            })()}
          </div>
        </nav>

        {/* Account Section - Fixed at bottom (admin-appropriate: org name + logout) */}
        <SidebarAccountMenu user={user} onLogout={onLogout} isCollapsed={isCollapsed} accountLabel={accountLabel} />
      </aside>
    </>
  );
}
